import React, { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { products, categories, getProductsByCategory, getSaleProducts, getFeaturedProducts } from '../data/products';

function Products() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const [sortBy, setSortBy] = useState('featured');

  const categoryInfo = categories.find((c) => c.id === category);

  const filteredProducts = useMemo(() => {
    let result;
    
    if (category) {
      result = getProductsByCategory(category);
    } else if (filter === 'sale') {
      result = getSaleProducts();
    } else if (filter === 'bestseller') {
      result = getFeaturedProducts();
    } else {
      result = products;
    }

    // Sort products
    switch (sortBy) {
      case 'price-low':
        return [...result].sort((a, b) => a.price - b.price);
      case 'price-high':
        return [...result].sort((a, b) => b.price - a.price);
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      case 'newest':
        return [...result].reverse();
      default:
        return result;
    }
  }, [category, filter, sortBy]);

  const getPageTitle = () => {
    if (categoryInfo) return categoryInfo.name;
    if (filter === 'sale') return 'Sale';
    if (filter === 'bestseller') return 'Best Sellers';
    return 'All Pillows';
  };

  const getPageDescription = () => {
    if (categoryInfo) return categoryInfo.description;
    if (filter === 'sale') return 'Limited time offers on our premium pillows';
    if (filter === 'bestseller') return 'Our most loved pillows by happy customers';
    return 'Explore our complete collection of premium pillows';
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs
            sx={{
              mb: 2,
              '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' },
            }}
          >
            <Link
              component={RouterLink}
              to="/"
              color="inherit"
              underline="hover"
              sx={{ opacity: 0.8 }}
            >
              Home
            </Link>
            <Link
              component={RouterLink}
              to="/products"
              color="inherit"
              underline="hover"
              sx={{ opacity: category ? 0.8 : 1 }}
            >
              Shop
            </Link>
            {categoryInfo && (
              <Typography color="inherit">{categoryInfo.name}</Typography>
            )}
          </Breadcrumbs>
          
          <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>
            {getPageTitle()}
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 1, opacity: 0.9, maxWidth: 600 }}
          >
            {getPageDescription()}
          </Typography>
        </Container>
      </Box>

      {/* Products Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Filters and Sort */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredProducts.length} products
            </Typography>
            {(category || filter) && (
              <Chip
                label={getPageTitle()}
                onDelete={() => window.location.href = '/products'}
                size="small"
              />
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="featured">Featured</MenuItem>
              <MenuItem value="price-low">Price: Low to High</MenuItem>
              <MenuItem value="price-high">Price: High to Low</MenuItem>
              <MenuItem value="rating">Highest Rated</MenuItem>
              <MenuItem value="newest">Newest</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Category filters */}
        {!category && !filter && (
          <Box sx={{ mb: 4, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <Chip
                key={cat.id}
                label={cat.name}
                component={RouterLink}
                to={cat.path}
                clickable
                variant="outlined"
                sx={{
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderColor: 'primary.main',
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* Products */}
        {filteredProducts.length > 0 ? (
          <Grid container spacing={3}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <ProductCard product={product} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No products found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your filters or browse all products.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default Products;
